// Label Positioning Helpers
//
// This module provides utility functions for intelligent label positioning in Chart.js charts.
// It handles collision detection, dynamic offset calculation, and scoring of positioning options
// to ensure labels don't overlap with other chart elements while staying within chart boundaries.

import type { LabelEdges, PositioningOption, SpaceMetrics, AlignmentResult, ChartArea, Range } from './types'

// Constants for collision detection and positioning
const MIN_SAFE_CLEARANCE = 5 // Minimum gap (in pixels) between labels to avoid visual crowding
const COLLISION_BUFFER = 5 // Extra pixels to add when adjusting offset to resolve collision
const LABEL_BORDER_AND_PADDING = 6 // Total pixels for border + padding around label text

// Scoring system for choosing LEFT vs RIGHT label positioning
// Example: LEFT has 15px clearance, 20px extra space → score = 15×2 + 20×0.5 + 100 = 140
//          RIGHT would overlap → score = -500
//          → LEFT wins (140 > -500)
const SCORE_WEIGHTS = {
  CLEARANCE_MULTIPLIER: 2, // Distance from headcount label (15px → +30 points)
  EXTRA_SPACE_MULTIPLIER: 0.5, // Room beyond what's needed (20px → +10 points)
  SAFE_CLEARANCE_BONUS: 100, // Bonus for comfortable spacing (≥5px clearance)

  // Penalties (negative values)
  COLLISION_PENALTY: -500, // Overlaps with headcount (bad, but might be only option)
  OUTSIDE_CHART_PENALTY: -1000, // Goes beyond chart edge (instant reject)
  INSUFFICIENT_SPACE_PENALTY: -900 // Not enough room (almost instant reject)
}

// Check if two ranges overlap
export const checkRangeOverlap = (range1: Range, range2: Range): boolean => {
  const [left1, right1] = range1
  const [left2, right2] = range2
  return left1 <= right2 && right1 >= left2
}

// Calculate minimum distance between two ranges
// Returns negative value if overlapping (amount of overlap)
// Returns positive value if not overlapping (distance between ranges)
export const calculateMinDistance = (range1: Range, range2: Range): number => {
  const [left1, right1] = range1
  const [left2, right2] = range2

  if (checkRangeOverlap(range1, range2)) {
    // Overlapping - return negative (amount of overlap)
    const overlapLeft = Math.max(left1, left2)
    const overlapRight = Math.min(right1, right2)
    return -(overlapRight - overlapLeft)
  }

  // Not overlapping - return distance
  if (right1 < left2) {
    return left2 - right1
  } else {
    return left1 - right2
  }
}

// Calculate dynamic offset based on clearance to avoid collision
export const calculateDynamicOffset = (baseClearance: number, baseOffset: number): number => {
  return baseClearance < 0 ? baseOffset + Math.abs(baseClearance) + COLLISION_BUFFER : baseOffset
}

// Score a positioning option (higher score = better position)
// Penalties: -1000 (outside chart), -900 (no space), -500 (collision)
// Rewards: clearance×2 + extraSpace×0.5 + safetyBonus(100)
export const scorePositioningOption = (option: PositioningOption): number => {
  // Instant disqualifiers
  if (!option.fitsInChart) return SCORE_WEIGHTS.OUTSIDE_CHART_PENALTY
  if (option.spaceNeeded > option.spaceAvailable) return SCORE_WEIGHTS.INSUFFICIENT_SPACE_PENALTY

  let score = 0

  if (option.collidesWithHeadcount) {
    score += SCORE_WEIGHTS.COLLISION_PENALTY
  }

  score += option.clearance * SCORE_WEIGHTS.CLEARANCE_MULTIPLIER
  score += (option.spaceAvailable - option.spaceNeeded) * SCORE_WEIGHTS.EXTRA_SPACE_MULTIPLIER

  if (option.clearance >= MIN_SAFE_CLEARANCE) {
    score += SCORE_WEIGHTS.SAFE_CLEARANCE_BONUS
  }

  return score
}

// Measure text width using canvas context
export const measureTextWidth = ({
  canvasCtx,
  text,
  fontSize
}: {
  canvasCtx: CanvasRenderingContext2D
  text: string
  fontSize: number
}): number => {
  canvasCtx.save()
  canvasCtx.font = `600 ${fontSize}px sans-serif`
  const width = canvasCtx.measureText(text).width
  canvasCtx.restore()
  return width
}

// Calculate total label width including padding and borders
export const calculateTotalLabelWidth = (textWidth: number, horizontalPadding: number): number => {
  return textWidth + horizontalPadding * 2 + LABEL_BORDER_AND_PADDING
}

// Calculate label edges based on positioning direction
// Used for both headcount labels (inside vs outside bar) and per-stakeholder labels (left vs right)
export const calculateLabelEdges = ({
  anchorX,
  labelWidth,
  offset,
  positionLeft
}: {
  anchorX: number
  labelWidth: number
  offset: number
  positionLeft: boolean
}): LabelEdges => {
  if (positionLeft) {
    // Position left of anchor
    return {
      leftEdge: anchorX - offset - labelWidth,
      rightEdge: anchorX - offset
    }
  } else {
    // Position right of anchor
    return {
      leftEdge: anchorX + offset,
      rightEdge: anchorX + offset + labelWidth
    }
  }
}

// Calculate space and fit metrics for a given side
const calculateSpaceMetrics = ({
  isLeft,
  perStakeholderX,
  chartArea,
  leftEdge,
  rightEdge,
  offset,
  totalWidth
}: {
  isLeft: boolean
  perStakeholderX: number
  chartArea: ChartArea
  leftEdge: number
  rightEdge: number
  offset: number
  totalWidth: number
}): SpaceMetrics => {
  return {
    spaceAvailable: isLeft ? perStakeholderX - chartArea.left : chartArea.right - perStakeholderX,
    spaceNeeded: offset + totalWidth,
    fitsInChart: isLeft ? leftEdge >= chartArea.left : rightEdge <= chartArea.right
  }
}

// Calculate positioning option for a specific side (left or right)
export const calculateSidePositioningOption = ({
  side,
  perStakeholderX,
  perStakeholderTotalWidth,
  headcountLeftEdge,
  headcountRightEdge,
  baseOffset,
  chartArea
}: {
  side: 'left' | 'right'
  perStakeholderX: number
  perStakeholderTotalWidth: number
  headcountLeftEdge: number
  headcountRightEdge: number
  baseOffset: number
  chartArea: ChartArea
}): PositioningOption => {
  const isLeft = side === 'left'

  // Calculate initial label position to determine clearance
  const initialEdges = calculateLabelEdges({
    anchorX: perStakeholderX,
    labelWidth: perStakeholderTotalWidth,
    offset: baseOffset,
    positionLeft: isLeft
  })
  const clearance = calculateMinDistance(
    [initialEdges.leftEdge, initialEdges.rightEdge],
    [headcountLeftEdge, headcountRightEdge]
  )

  // Adjust offset if collision detected
  const dynamicOffset = calculateDynamicOffset(clearance, baseOffset)

  // Recalculate edges with adjusted offset
  const finalEdges = calculateLabelEdges({
    anchorX: perStakeholderX,
    labelWidth: perStakeholderTotalWidth,
    offset: dynamicOffset,
    positionLeft: isLeft
  })

  // Calculate space and fit metrics
  const metrics = calculateSpaceMetrics({
    isLeft,
    perStakeholderX,
    chartArea,
    leftEdge: finalEdges.leftEdge,
    rightEdge: finalEdges.rightEdge,
    offset: dynamicOffset,
    totalWidth: perStakeholderTotalWidth
  })

  return {
    align: side,
    offset: dynamicOffset,
    ...finalEdges,
    ...metrics,
    clearance,
    collidesWithHeadcount: clearance < 0
  }
}

// Select the best alignment from available options
export const selectBestAlignment = ({
  leftOption,
  rightOption,
  centerCollides,
  centerFits,
  leftScore,
  rightScore
}: {
  leftOption: PositioningOption
  rightOption: PositioningOption
  centerCollides: boolean
  centerFits: boolean
  leftScore: number
  rightScore: number
}): AlignmentResult => {
  // Priority 1: If center works (no collision AND fits), use it
  if (!centerCollides && centerFits) {
    return { alignment: 'center', chosenOption: null }
  }

  // Priority 2: Choose between LEFT and RIGHT
  // Always prefer an option that fits over one that doesn't
  if (leftOption.fitsInChart && !rightOption.fitsInChart) {
    return { alignment: leftOption.align, chosenOption: leftOption }
  }

  if (rightOption.fitsInChart && !leftOption.fitsInChart) {
    return { alignment: rightOption.align, chosenOption: rightOption }
  }

  // Both fit or both don't fit - use scores
  if (leftScore > rightScore) {
    return { alignment: leftOption.align, chosenOption: leftOption }
  }

  return { alignment: rightOption.align, chosenOption: rightOption }
}
